import { useMemo, useState } from 'react';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent
} from '@carbon/react';

import { useNotifications } from 'core/notifications';
import { USER_ROLE_LABEL } from 'core/user/role';
import UserModal from 'features/admin/users/UserModal';
import useMutateAddUser from 'shared/api/users/useMutateAddUser';
import useMutateEditUser from 'shared/api/users/useMutateEditUser';
import useMutateRemoveUser from 'shared/api/users/useMutateRemoveUser';
import useUsersQuery from 'shared/api/users/useUsersQuery';
import ActionButton from 'shared/components/ActionButton';
import TableToolbarCell from 'shared/components/data-table/TableToolbarCell';
import OverlayLoader from 'shared/components/OverlayLoader';
import Pagination, { usePagination } from 'shared/components/Pagination';
import styles from 'theme/features/admin/users/users-administration.module.scss';

const UsersAdministration = () => {
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userEditIndex, setUserEditIndex] = useState(null);

  const { data: users, refetch: refetchUsers, isRefetching: isRefetchingUsers } = useUsersQuery();

  const pagination = usePagination();
  const usersPage = useMemo(
    () => users?.slice(pagination.pageSize * (pagination.page - 1), pagination.pageSize * pagination.page) ?? [],
    [users, pagination.page, pagination.pageSize]
  );

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setUserEditIndex(null);
  };

  const addUserMutation = useMutateAddUser({
    onError: () => showErrorNotification({ description: 'Failed to add a user' }),
    onSuccess: () => {
      closeUserModal();
      refetchUsers();
      showSuccessNotification({ description: 'User added successfully' });
    }
  });

  const editUserMutation = useMutateEditUser({
    onError: () => showErrorNotification({ description: 'Failed to edit the user' }),
    onSuccess: () => {
      closeUserModal();
      refetchUsers();
      showSuccessNotification({ description: 'User edited successfully' });
    }
  });

  const removeUserMutation = useMutateRemoveUser({
    onError: () => showErrorNotification({ description: 'Failed to remove the user' }),
    onSuccess: () => {
      if (usersPage.length === 1) {
        pagination.setPage(Math.max(pagination.page - 1, 1));
      }

      refetchUsers();
      showSuccessNotification({ description: 'User removed successfully' });
    }
  });

  const handleSubmit = (user) => {
    if (userEditIndex === null) {
      addUserMutation.mutate(user);
    } else {
      editUserMutation.mutate({
        id: usersPage[userEditIndex].id,
        user
      });
    }
  };

  const handleEditUser = (index) => {
    setIsUserModalOpen(true);
    setUserEditIndex(index);
  };

  const tableData =
    usersPage.map((x) => ({
      id: x.id.toString(),
      name: x.name,
      email: x.email,
      role: USER_ROLE_LABEL[x.role]
    })) ?? [];

  const headers = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'role', header: 'Role' }
  ];

  return (
    <>
      <UserModal
        open={isUserModalOpen}
        user={userEditIndex === null ? null : usersPage[userEditIndex]}
        onClose={closeUserModal}
        onSubmit={handleSubmit}
        isLoading={addUserMutation.isLoading || editUserMutation.isLoading}
      />
      <DataTable
        headers={headers}
        rows={tableData}
        size="sm"
        render={({ rows, getTableContainerProps, getToolbarProps, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer
            title="Users Administration"
            description="Add, update or remove users in the application"
            className={styles.wrapper}
            {...getTableContainerProps()}
          >
            <OverlayLoader
              small={rows.length === 0}
              isLoading={!users || isRefetchingUsers || removeUserMutation.isLoading}
            />
            <TableToolbar {...getToolbarProps()} size="sm">
              <TableToolbarContent>
                <ActionButton onClick={() => setIsUserModalOpen(true)}>Add a User</ActionButton>
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                  <TableToolbarCell.Header />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                    <TableToolbarCell>
                      <TableToolbarCell.MenuItem itemText="Edit" onClick={() => handleEditUser(index)} />
                      <TableToolbarCell.MenuItem
                        isDelete
                        itemText="Remove"
                        onClick={() => removeUserMutation.mutate(row.id)}
                      />
                    </TableToolbarCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              pageSize={pagination.pageSize}
              page={pagination.page}
              isLastPage={!users || users.length <= pagination.pageSize * pagination.page}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </TableContainer>
        )}
      />
    </>
  );
};

export default UsersAdministration;
